import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "../../../../components/Header/Header";
import img1 from "./QuestionnairePic.png";
import api from "../../../chat/api";
import {
  MoreVertical,
  Loader2,
  Plus,
  Folder,
  Upload,
  Download,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";


import SelectProjectModal from "./SelectProjectModal";
import UploadModal from "./UploadModal";
import { getFileIcon } from "../utils/fileIcons";


const QuestionnairePage = () => {
  const { projectId, orgId } = useParams();
  const navigate = useNavigate();
  const folderId = projectId;
  const [questionnaires, setQuestionnaires] = useState([]);
  const [permission, setPermission] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Modal States
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);


  // File Upload State
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);


  // Dropdown state for Header "Add" button
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);


  // Action Menu State (for Table Rows)
  const [actionMenuOpenId, setActionMenuOpenId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const rowMenuRef = useRef(null);
  const [hoveredAction, setHoveredAction] = useState(null);


  // Delete States
  const [deletingId, setDeletingId] = useState(null);
  const [deleteSuccessId, setDeleteSuccessId] = useState(null);
  const deletingIdRef = useRef(null);


  // Keep a ref of deletingId to prevent closing the menu when clicking outside during deletion
  useEffect(() => {
    deletingIdRef.current = deletingId;
  }, [deletingId]);


  // Reset hovered action when the menu changes or closes
  useEffect(() => {
    setHoveredAction(null);
  }, [actionMenuOpenId]);


  const fetchQuestionnaires = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/api/v1/rfp/folders/${folderId}/questionnaires/`
      );


      if (response.status === 200) {
        const data = response.data;
        setPermission(data.permission); // Set the folder permission
        const rawList = data.documents || data.results || [];


        const mappedData = rawList.map((item) => ({
          id: item.id,
          name: item.name || "Untitled Questionnaire",
          fileType: item.file_type || item.name?.split(".").pop() || "pdf",
          questionsAnswered: item.questions_answered || "0/0",
          // Store raw date for sorting
          rawDate: item.uploaded_at ? new Date(item.uploaded_at) : new Date(0), 
          // Format date for display
          uploadedOn: item.uploaded_at 
            ? new Date(item.uploaded_at).toLocaleDateString() 
            : new Date().toLocaleDateString(),
          status: item.status || "-",
          url: item.url || "", // Map URL for download
        }));


        // Sort by rawDate descending (Newest first)
        const sortedData = mappedData.sort((a, b) => b.rawDate - a.rawDate);


        setQuestionnaires(sortedData);
      } else {
        setQuestionnaires([]);
      }


      setLoading(false);
    } catch (error) {
      console.error("Error fetching questionnaires:", error);
      toast.error("Failed to load questionnaires.");
      setQuestionnaires([]);
      setLoading(false);
    }
  }, [folderId]);


  useEffect(() => {
    fetchQuestionnaires();
  }, [fetchQuestionnaires]);


  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (rowMenuRef.current && !rowMenuRef.current.contains(event.target)) {
        if (!deletingIdRef.current) {
          setActionMenuOpenId(null);
        }
      }
    };


    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  // --- Download Logic ---
  const handleDownload = async (e, url, fileName) => {
    e.stopPropagation();
    setActionMenuOpenId(null);


    if (!url) {
      toast.error("Download link not available.");
      return;
    }

    const name = fileName || "document";
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
      // Could not fetch the file for a renamed download (e.g. CORS) —
      // fall back to opening it in a separate tab.
      const opened = window.open(url, "_blank", "noopener,noreferrer");
      if (!opened) toast.error("Failed to download file.");
    }
  };


  // --- Delete Logic ---
  const handleDelete = async (docId, event) => {
    event.stopPropagation();
    setDeletingId(docId);


    try {
      const res = await api.post(
        "/api/v1/rfp/questionnaires/delete/",
        { folder_id: Number(folderId), doc_ids: [docId] },
        { headers: { "Content-Type": "application/json" } }
      );


      if (res.status === 200 || res.status === 204) {
        setDeletingId(null);
        setDeleteSuccessId(docId);
        toast.success("Document deleted successfully!");


        setTimeout(() => {
          setDeleteSuccessId(null);
          // Only close the menu if the user hasn't opened another row's menu
          setActionMenuOpenId((prev) => (prev === docId ? null : prev));
          fetchQuestionnaires();
        }, 2000);
      }
    } catch (error) {
      console.error("Error deleting documents:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete document(s). Please try again.";
      toast.error(errorMessage);
      setDeletingId(null);
      setActionMenuOpenId((prev) => (prev === docId ? null : prev));
    }
  };


  // --- File Upload Logic ---


  // Trigger hidden file input
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };


  // Handle file selection from device
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setIsUploadModalOpen(true);
    }
    event.target.value = null; // Reset input
  };


  // Handle "Cancel" or "Remove" in Modal
  const handleUploadCancel = () => {
    setSelectedFile(null);
    setIsUploadModalOpen(false);
  };


  // Called when UploadModal finishes successfully
  const handleUploadSuccess = () => {
    setSelectedFile(null);
    fetchQuestionnaires();
  };


  // --- End File Upload Logic ---


  // Handle Import Success from Project Modal
  const handleImportSuccess = () => {
    fetchQuestionnaires();
  };


  // Toggle Row Menu Logic
  const toggleRowMenu = (id, event) => {
    event.stopPropagation(); // Prevents row click
    event.preventDefault();


    if (actionMenuOpenId === id) {
      setActionMenuOpenId(null);
      return;
    }


    const menuHeight = 120;
    const rect = event.currentTarget.getBoundingClientRect();
    const bottomSpace = window.innerHeight - rect.bottom;
    const openUpwards = bottomSpace < menuHeight;


    setMenuPosition({
      top: openUpwards ? rect.top - menuHeight : rect.bottom,
      left: rect.left - 140,
    });
    setActionMenuOpenId(id);
  };
  
  // Handle Row Click Navigation
  const handleRowClick = (docId) => {
    navigate(`/rfp-projects/project/${projectId}/org/${orgId}/questionnaire/${docId}`);
  };


  // return (
  //   <div className="Contents-Full-Container">
  //     {/* Hidden File Input */}
  //     <input
  //       type="file"
  //       ref={fileInputRef}
  //       onChange={handleFileChange}
  //       style={{ display: "none" }}
  //       accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
  //     />


  //     {/* Navigation Header */}
  //     <div>
  //       <Header
  //         icon={"/arrow-back.svg"}
  //         name="Back to Project"
  //         onClick={() =>
  //           navigate(`/rfp-projects/project/${projectId}/org/${orgId}`)
  //         }
  //       />
  //     </div>


  //     {/* Main Content Container */}
  //     <div
  //       className="flex flex-col bg-white rounded-md font-outfit shadow-sm border border-gray-100"
  //       style={{
  //         marginLeft: "195px",
  //         marginTop: "10px",
  //         width: "calc(100% - 205px)",
  //         height: "calc(100vh - 100px)",
  //       }}
  //     >
  //       {/* Internal Header Section */}
  //       <div className="px-8 py-5 border-b border-gray-200 flex justify-between items-center">
  //         <div>
  //           <h1 className="text-xl !font-semibold text-[#18101C]">
  //             Questionnaire Manager
  //           </h1>
  //           <p className="text-[14px] text-[#52525B] font-normal mt-1">
  //             Extract and answer questions from RFPs and Questionnaire files
  //           </p>
  //         </div>


  //         {/* Show Add button in header only if we have data */}
  //         {!loading && questionnaires.length > 0 && (
  //           <div className="relative" ref={dropdownRef}>
  //             <button
  //               onClick={() => setIsDropdownOpen(!isDropdownOpen)}
  //               className="flex items-center gap-2 bg-[#C95EBE] text-white px-4 py-2 rounded-lg text-[14px] font-medium transition-colors"
  //             >
  //               <Plus size={18} />
  //               Add Questionnaire
  //             </button>


  //             {/* Dropdown Menu */}
  //             {isDropdownOpen && (
  //               <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden py-1">
  //                 <button
  //                   onClick={() => {
  //                     setIsProjectModalOpen(true);
  //                     setIsDropdownOpen(false);
  //                   }}
  //                   className="w-full text-left px-3 py-2 text-[14px] text-[#71717A] hover:bg-gray-50 flex items-center gap-3 transition-colors"
  //                 >
  //                   <Folder size={18} className="text-[#71717A]" />
  //                   Select from Project
  //                 </button>
  //                 <button
  //                   onClick={() => {
  //                     handleUploadClick();
  //                     setIsDropdownOpen(false);
  //                   }}
  //                   className="w-full text-left px-3 py-2 text-[14px] text-[#71717A] hover:bg-gray-50 flex items-center gap-3 transition-colors"
  //                 >
  //                   <Upload size={18} className="text-[#71717A]" />
  //                   Upload from Device
  //                 </button>
  //               </div>
  //             )}
  //           </div>
  //         )}
  //       </div>


  //       {/* Content Area */}
  //       <div className="flex-1 overflow-hidden flex flex-col p-3">
  //         {/* Loading State */}
  //         {loading ? (
  //           <div className="flex-1 flex flex-col items-center justify-center gap-3 text-[#52525B]">
  //             <Loader2 className="w-8 h-8 animate-spin text-[#C95EBE]" />
  //             <span className="font-medium">Fetching Questionnaires...</span>
  //           </div>
  //         ) : questionnaires.length > 0 ? (
  //           /* Table UI */
  //           <div className="flex-1 overflow-auto rounded-xl border border-gray-100">
  //             <table className="w-full text-left border-collapse">
  //               <thead className="sticky top-0 z-10">
  //                 <tr className="bg-[#f4f4f5] text-[#52525B] text-[13px] font-medium uppercase tracking-wide">
  //                   <th className="py-2 px-6 rounded-tl-xl font-semibold">
  //                     File Name
  //                   </th>
  //                   <th className="py-2 px-6 font-semibold">
  //                     Questions Answered
  //                   </th>
  //                   <th className="py-2 px-6 font-semibold">Uploaded On</th>
  //                   <th className="py-2 px-6 font-semibold text-center">
  //                     Status
  //                   </th>
  //                   <th className="py-2 px-6 rounded-tr-xl text-right"></th>
  //                 </tr>
  //               </thead>
  //               <tbody className="divide-y divide-gray-100 bg-white">
  //                 {questionnaires.map((item) => (
  //                   <tr
  //                     key={item.id}
  //                     onClick={() => handleRowClick(item.id)}
  //                     className="group hover:bg-gray-50 transition-colors cursor-pointer"
  //                   >
  //                     {/* File Name */}
  //                     <td className="py-3 px-6">
  //                       <div className="flex items-center gap-3">
  //                         {getFileIcon(item.fileType)}
  //                         <span className="text-[#18101C] font-medium text-[14px]">
  //                           {item.name}
  //                         </span>
  //                       </div>
  //                     </td>


  //                     {/* Questions Answered */}
  //                     <td className="py-3 px-6 text-[14px] text-[#18101C] font-medium">
  //                       {item.questionsAnswered}
  //                     </td>


  //                     {/* Uploaded On */}
  //                     <td className="py-3 px-6 text-[14px] text-[#18101C]">
  //                       {item.uploadedOn}
  //                     </td>


  //                     {/* Status */}
  //                     <td className="py-3 px-6 text-center">
  //                       <div className="flex justify-center">
  //                         {item.status.toLowerCase() === "extracting" ? (
  //                           <div className="flex items-center justify-center gap-2 w-[140px] py-1.5 rounded-full bg-[#E4E4E7] text-[#52525B] text-[13px] font-semibold">
  //                             <Loader2 className="w-4 h-4 animate-spin text-[#52525B]" />
  //                             Extracting
  //                           </div>
  //                         ) : (
  //                           <div className="flex items-center justify-center gap-2 w-[160px] py-1.5 rounded-full bg-[#DCFCE7] text-[#16A34A] text-[13px] font-semibold">
  //                             <svg
  //                               xmlns="http://www.w3.org/2000/svg"
  //                               width="16"
  //                               height="16"
  //                               viewBox="0 0 16 16"
  //                               fill="none"
  //                             >
  //                               <path
  //                                 d="M6.7785 8.8395L5.44163 7.50244C5.26175 7.32256 5.03731 7.23263 4.76831 7.23263C4.49931 7.23263 4.27481 7.32256 4.09481 7.50244C3.91494 7.68231 3.82675 7.90681 3.83025 8.17594C3.83387 8.44494 3.92563 8.66937 4.1055 8.84925L6.0735 10.8172C6.2755 11.0166 6.51119 11.1163 6.78056 11.1163C7.04994 11.1163 7.28431 11.0166 7.48369 10.8172L11.5622 6.73856C11.7422 6.55869 11.8322 6.336 11.8322 6.0705C11.8322 5.805 11.7422 5.58231 11.5622 5.40244C11.3823 5.22256 11.1579 5.13262 10.8889 5.13262C10.6199 5.13262 10.3954 5.22256 10.2156 5.40244L6.7785 8.8395ZM7.86038 15.7207C6.76638 15.7207 5.74194 15.5147 4.78706 15.1027C3.83231 14.6907 3.00181 14.1316 2.29556 13.4252C1.58919 12.7189 1.03 11.8884 0.618 10.9337C0.206 9.97881 0 8.95437 0 7.86037C0 6.76637 0.206 5.74194 0.618 4.78706C1.03 3.83231 1.58919 3.00181 2.29556 2.29556C3.00181 1.58919 3.83231 1.03 4.78706 0.617999C5.74194 0.205999 6.76638 0 7.86038 0C8.95438 0 9.97881 0.205999 10.9337 0.617999C11.8884 1.03 12.7189 1.58919 13.4252 2.29556C14.1316 3.00181 14.6907 3.83231 15.1027 4.78706C15.5147 5.74194 15.7207 6.76637 15.7207 7.86037C15.7207 8.95437 15.5147 9.97881 15.1027 10.9337C14.6907 11.8884 14.1316 12.7189 13.4252 13.4252C12.7189 14.1316 11.8884 14.6907 10.9337 15.1027C9.97881 15.5147 8.95438 15.7207 7.86038 15.7207Z"
  //                                 fill="#009428"
  //                               />
  //                             </svg>
  //                             Ready for Review
  //                           </div>
  //                         )}
  //                       </div>
  //                     </td>


  //                     {/* Actions */}
  //                     <td className="py-3 px-6 text-right relative">
  //                       <button
  //                         onClick={(e) => toggleRowMenu(item.id, e)}
  //                         className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
  //                       >
  //                         <MoreVertical size={18} />
  //                       </button>


  //                       {/* Action Menu Popover */}
  //                       {actionMenuOpenId === item.id && (
  //                         <div
  //                           ref={rowMenuRef}
  //                           className="fixed z-[9999] bg-white rounded-xl shadow-lg p-1.5 min-w-[160px] border border-gray-100"
  //                           style={{
  //                             top: `${menuPosition.top}px`,
  //                             left: `${menuPosition.left}px`,
  //                           }}
  //                         >
  //                           <div
  //                             onMouseEnter={() => setHoveredAction("download")}
  //                             onMouseLeave={() => setHoveredAction(null)}
  //                             onClick={(e) => handleDownload(e, item.url, item.name)}
  //                             className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150 whitespace-nowrap ${
  //                               hoveredAction === "download"
  //                                 ? "bg-gray-100 text-gray-900"
  //                                 : "text-gray-500"
  //                             }`}
  //                           >
  //                             <div className="w-5 h-5 flex-shrink-0 text-gray-500">
  //                               <Download size={18} />
  //                             </div>
  //                             <span className="text-sm font-medium">
  //                               Download
  //                             </span>
  //                           </div>

  //                           {/* Conditionally render delete based on permissions */}
  //                           {(permission === "owner" || permission === "admin") && (
  //                             <div
  //                               onMouseEnter={() => setHoveredAction("delete")}
  //                               onMouseLeave={() => setHoveredAction(null)}
  //                               onClick={(e) => {
  //                                 if (deletingId === item.id || deleteSuccessId === item.id) return;
  //                                 handleDelete(item.id, e);
  //                               }}
  //                               className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150 whitespace-nowrap ${
  //                                 hoveredAction === "delete"
  //                                   ? "bg-red-50 text-red-600"
  //                                   : "text-gray-500"
  //                               }`}
  //                             >
  //                               <div className="w-5 h-5 flex-shrink-0">
  //                                 {deletingId === item.id ? (
  //                                   <Loader2 size={18} className="animate-spin text-red-600" />
  //                                 ) : (
  //                                   <Trash2
  //                                     size={18}
  //                                     className={
  //                                       deleteSuccessId === item.id
  //                                         ? "text-green-600"
  //                                         : hoveredAction === "delete"
  //                                         ? "text-red-600"
  //                                         : "text-gray-500"
  //                                     }
  //                                   />
  //                                 )}
  //                               </div>
  //                               <span
  //                                 className={`text-sm font-medium ${
  //                                   deleteSuccessId === item.id ? "text-green-600" : ""
  //                                 }`}
  //                               >
  //                                 {deletingId === item.id
  //                                   ? "Deleting..."
  //                                   : deleteSuccessId === item.id
  //                                   ? "Deleted"
  //                                   : "Delete"}
  //                               </span>
  //                             </div>
  //                           )}
  //                         </div>
  //                       )}
  //                     </td>
  //                   </tr>
  //                 ))}
  //               </tbody>
  //             </table>
  //           </div>
  //         ) : (
  //           /* Empty State */
  //           <div className="flex-1 flex flex-col items-center justify-center p-8">
  //             <div className="mb-6">
  //               <img
  //                 src={img1}
  //                 alt="No questionnaires"
  //                 className="w-64 h-auto object-contain"
  //               />
  //             </div>


  //             <h2 className="text-lg font-semibold text-[#18101C] mb-2">
  //               No Questionnaires Found!
  //             </h2>
  //             <p className="text-[#52525B] font-normal text-center mb-8 max-w-lg">
  //               Add questionnaire files from your project or upload new ones to
  //               start
  //             </p>


  //             {/* Action Buttons */}
  //             <div className="flex items-center gap-4">
  //               <button
  //                 onClick={() => setIsProjectModalOpen(true)}
  //                 className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#C95EBE] text-[#C95EBE] font-medium hover:bg-purple-50 transition-colors"
  //               >
  //                 <Folder size={18} />
  //                 Select from Project
  //               </button>


  //               <button
  //                 onClick={handleUploadClick}
  //                 className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#C95EBE] text-white font-medium hover:bg-[#b04da6] transition-colors shadow-sm"
  //               >
  //                 <Upload size={18} />
  //                 Upload File
  //               </button>
  //             </div>
  //           </div>
  //         )}
  //       </div>
  //     </div>


  //     {/* Render the Project Select Modal */}
  //     <SelectProjectModal
  //       isOpen={isProjectModalOpen}
  //       onClose={() => setIsProjectModalOpen(false)}
  //       folderId={folderId}
  //       onImport={handleImportSuccess}
  //     />


  //     {/* Render the New Upload Modal */}
  //     <UploadModal
  //       isOpen={isUploadModalOpen}
  //       onClose={handleUploadCancel}
  //       file={selectedFile}
  //       onRemove={handleUploadCancel}
  //       folderId={folderId}
  //       onUploadSuccess={handleUploadSuccess}
  //     />
  //   </div>
  // );
  return (
    <div className="h-full w-full flex flex-col overflow-hidden bg-[#FEF9F7]">
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
      />
  
      {/* Navigation Header */}
      <div className="flex-shrink-0">
        <Header
          icon={"/arrow-back.svg"}
          name="Back to Project"
          onClick={() =>
            navigate(`/rfp-projects/project/${projectId}/org/${orgId}`)
          }
        />
      </div>
  
      {/* Main Content Container */}
      <div className="flex-1 min-h-0 w-full px-4 pt-3 pb-4 box-border">
        <div className="flex flex-col bg-white rounded-md font-outfit shadow-sm border border-gray-100 h-full min-h-0 overflow-hidden">
          {/* Internal Header Section */}
          <div className="px-8 py-5 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
            <div>
              <h1 className="text-xl !font-semibold text-[#18101C]">
                Questionnaire Manager
              </h1>
              <p className="text-[14px] text-[#52525B] font-normal mt-1">
                Extract and answer questions from RFPs and Questionnaire files
              </p>
            </div>
  
            {/* Show Add button in header only if we have data */}
            {!loading && questionnaires.length > 0 && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 bg-[#C95EBE] text-white px-4 py-2 rounded-lg text-[14px] font-medium transition-colors"
                >
                  <Plus size={18} />
                  Add Questionnaire
                </button>
  
                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden py-1">
                    <button
                      onClick={() => {
                        setIsProjectModalOpen(true);
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-[14px] text-[#71717A] hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <Folder size={18} className="text-[#71717A]" />
                      Select from Project
                    </button>
                    <button
                      onClick={() => {
                        handleUploadClick();
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-[14px] text-[#71717A] hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <Upload size={18} className="text-[#71717A]" />
                      Upload from Device
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
  
          {/* Content Area */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col p-3">
            {/* Loading State */}
            {loading ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 text-[#52525B]">
                <Loader2 className="w-8 h-8 animate-spin text-[#C95EBE]" />
                <span className="font-medium">Fetching Questionnaires...</span>
              </div>
            ) : questionnaires.length > 0 ? (
              /* Table UI */
              <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-gray-100">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-[#f4f4f5] text-[#52525B] text-[13px] font-medium uppercase tracking-wide">
                      <th className="py-2 px-6 rounded-tl-xl font-semibold">
                        File Name
                      </th>
                      <th className="py-2 px-6 font-semibold">
                        Questions Answered
                      </th>
                      <th className="py-2 px-6 font-semibold">Uploaded On</th>
                      <th className="py-2 px-6 font-semibold text-center">
                        Status
                      </th>
                      <th className="py-2 px-6 rounded-tr-xl text-right"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {questionnaires.map((item) => (
                      <tr
                        key={item.id}
                        onClick={() => handleRowClick(item.id)}
                        className="group hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        {/* File Name */}
                        <td className="py-3 px-6">
                          <div className="flex items-center gap-3">
                            {getFileIcon(item.fileType)}
                            <span className="text-[#18101C] font-medium text-[14px]">
                              {item.name}
                            </span>
                          </div>
                        </td>
  
                        {/* Questions Answered */}
                        <td className="py-3 px-6 text-[14px] text-[#18101C] font-medium">
                          {item.questionsAnswered}
                        </td>
  
                        {/* Uploaded On */}
                        <td className="py-3 px-6 text-[14px] text-[#18101C]">
                          {item.uploadedOn}
                        </td>
  
                        {/* Status */}
                        <td className="py-3 px-6 text-center">
                          <div className="flex justify-center">
                            {item.status.toLowerCase() === "extracting" ? (
                              <div className="flex items-center justify-center gap-2 w-[140px] py-1.5 rounded-full bg-[#E4E4E7] text-[#52525B] text-[13px] font-semibold">
                                <Loader2 className="w-4 h-4 animate-spin text-[#52525B]" />
                                Extracting
                              </div>
                            ) : (
                              <div className="flex items-center justify-center gap-2 w-[160px] py-1.5 rounded-full bg-[#DCFCE7] text-[#16A34A] text-[13px] font-semibold">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M6.7785 8.8395L5.44163 7.50244C5.26175 7.32256 5.03731 7.23263 4.76831 7.23263C4.49931 7.23263 4.27481 7.32256 4.09481 7.50244C3.91494 7.68231 3.82675 7.90681 3.83025 8.17594C3.83387 8.44494 3.92563 8.66937 4.1055 8.84925L6.0735 10.8172C6.2755 11.0166 6.51119 11.1163 6.78056 11.1163C7.04994 11.1163 7.28431 11.0166 7.48369 10.8172L11.5622 6.73856C11.7422 6.55869 11.8322 6.336 11.8322 6.0705C11.8322 5.805 11.7422 5.58231 11.5622 5.40244C11.3823 5.22256 11.1579 5.13262 10.8889 5.13262C10.6199 5.13262 10.3954 5.22256 10.2156 5.40244L6.7785 8.8395ZM7.86038 15.7207C6.76638 15.7207 5.74194 15.5147 4.78706 15.1027C3.83231 14.6907 3.00181 14.1316 2.29556 13.4252C1.58919 12.7189 1.03 11.8884 0.618 10.9337C0.206 9.97881 0 8.95437 0 7.86037C0 6.76637 0.206 5.74194 0.618 4.78706C1.03 3.83231 1.58919 3.00181 2.29556 2.29556C3.00181 1.58919 3.83231 1.03 4.78706 0.617999C5.74194 0.205999 6.76638 0 7.86038 0C8.95438 0 9.97881 0.205999 10.9337 0.617999C11.8884 1.03 12.7189 1.58919 13.4252 2.29556C14.1316 3.00181 14.6907 3.83231 15.1027 4.78706C15.5147 5.74194 15.7207 6.76637 15.7207 7.86037C15.7207 8.95437 15.5147 9.97881 15.1027 10.9337C14.6907 11.8884 14.1316 12.7189 13.4252 13.4252C12.7189 14.1316 11.8884 14.6907 10.9337 15.1027C9.97881 15.5147 8.95438 15.7207 7.86038 15.7207Z"
                                    fill="#009428"
                                  />
                                </svg>
                                Ready for Review
                              </div>
                            )}
                          </div>
                        </td>
  
                        {/* Actions */}
                        <td className="py-3 px-6 text-right relative">
                          <button
                            onClick={(e) => toggleRowMenu(item.id, e)}
                            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
                          >
                            <MoreVertical size={18} />
                          </button>
  
                          {/* Action Menu Popover */}
                          {actionMenuOpenId === item.id && (
                            <div
                              ref={rowMenuRef}
                              className="fixed z-[9999] bg-white rounded-xl shadow-lg p-1.5 min-w-[160px] border border-gray-100"
                              style={{
                                top: `${menuPosition.top}px`,
                                left: `${menuPosition.left}px`,
                              }}
                            >
                              <div
                                onMouseEnter={() => setHoveredAction("download")}
                                onMouseLeave={() => setHoveredAction(null)}
                                onClick={(e) => handleDownload(e, item.url, item.name)}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150 whitespace-nowrap ${
                                  hoveredAction === "download"
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-500"
                                }`}
                              >
                                <div className="w-5 h-5 flex-shrink-0 text-gray-500">
                                  <Download size={18} />
                                </div>
                                <span className="text-sm font-medium">
                                  Download
                                </span>
                              </div>
  
                              {(permission === "owner" || permission === "admin") && (
                                <div
                                  onMouseEnter={() => setHoveredAction("delete")}
                                  onMouseLeave={() => setHoveredAction(null)}
                                  onClick={(e) => {
                                    if (
                                      deletingId === item.id ||
                                      deleteSuccessId === item.id
                                    )
                                      return;
                                    handleDelete(item.id, e);
                                  }}
                                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150 whitespace-nowrap ${
                                    hoveredAction === "delete"
                                      ? "bg-red-50 text-red-600"
                                      : "text-gray-500"
                                  }`}
                                >
                                  <div className="w-5 h-5 flex-shrink-0">
                                    {deletingId === item.id ? (
                                      <Loader2
                                        size={18}
                                        className="animate-spin text-red-600"
                                      />
                                    ) : (
                                      <Trash2
                                        size={18}
                                        className={
                                          deleteSuccessId === item.id
                                            ? "text-green-600"
                                            : hoveredAction === "delete"
                                            ? "text-red-600"
                                            : "text-gray-500"
                                        }
                                      />
                                    )}
                                  </div>
                                  <span
                                    className={`text-sm font-medium ${
                                      deleteSuccessId === item.id
                                        ? "text-green-600"
                                        : ""
                                    }`}
                                  >
                                    {deletingId === item.id
                                      ? "Deleting..."
                                      : deleteSuccessId === item.id
                                      ? "Deleted"
                                      : "Delete"}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Empty State */
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <div className="mb-6">
                  <img
                    src={img1}
                    alt="No questionnaires"
                    className="w-64 h-auto object-contain"
                  />
                </div>
  
                <h2 className="text-lg font-semibold text-[#18101C] mb-2">
                  No Questionnaires Found!
                </h2>
                <p className="text-[#52525B] font-normal text-center mb-8 max-w-lg">
                  Add questionnaire files from your project or upload new ones to
                  start
                </p>
  
                {/* Action Buttons */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setIsProjectModalOpen(true)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#C95EBE] text-[#C95EBE] font-medium hover:bg-purple-50 transition-colors"
                  >
                    <Folder size={18} />
                    Select from Project
                  </button>
  
                  <button
                    onClick={handleUploadClick}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#C95EBE] text-white font-medium hover:bg-[#b04da6] transition-colors shadow-sm"
                  >
                    <Upload size={18} />
                    Upload File
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  
      {/* Render the Project Select Modal */}
      <SelectProjectModal
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        folderId={folderId}
        onImport={handleImportSuccess}
      />
  
      {/* Render the New Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={handleUploadCancel}
        file={selectedFile}
        onRemove={handleUploadCancel}
        folderId={folderId}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
  
};


export default QuestionnairePage;
